import React, { useState, useEffect, Suspense } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Home,
  Gift,
  History,
  Package,
  Warehouse,
  User,
  LogOut,
  Monitor,
  FileDown
} from 'lucide-react';
import { ROLE_LABELS, isAdmin } from '../lib/utils';

const MobileLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [receptionsCount, setReceptionsCount] = useState(0);
  
  // Charger le nombre de réceptions en attente
  useEffect(() => {
    const fetchReceptionsCount = async () => {
      try {
        const response = await api.get('/stats/dashboard');
        setReceptionsCount(response.data?.receptions_en_attente || 0);
      } catch (error) {
        console.error('Error fetching receptions count:', error);
      }
    };
    
    fetchReceptionsCount();
    // Rafraîchir toutes les 30 secondes
    const interval = setInterval(fetchReceptionsCount, 30000);
    return () => clearInterval(interval);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/mobile', label: 'Accueil', icon: Home },
    { path: '/mobile/historique', label: 'Historique', icon: History },
    { path: '/mobile/remettre', label: 'Remettre', icon: Gift, isMain: true },
    { path: '/mobile/reception', label: 'Réception', icon: Package },
    { path: '/mobile/stock', label: 'Stock', icon: Warehouse },
  ];

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Mobile header */}
      <header className="fixed top-0 left-0 right-0 h-14 bg-white border-b border-slate-200 z-40 px-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-indigo-700 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-sm">K</span>
          </div>
          <span className="font-semibold text-indigo-700">KADOLOGISTIQUE</span>
        </div>
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-2" data-testid="mobile-user-menu">
              <div className="w-7 h-7 bg-indigo-100 rounded-full flex items-center justify-center">
                <User className="h-4 w-4 text-indigo-700" />
              </div>
              <span className="text-sm font-medium max-w-[100px] truncate">
                {user?.prenom}
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div>
                <p className="font-medium">{user?.prenom} {user?.nom}</p>
                <p className="text-xs text-slate-500 font-normal">{ROLE_LABELS[user?.role]}</p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {isAdmin(user) && (
              <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="switch-backoffice">
                <Monitor className="mr-2 h-4 w-4" />
                Back-office
              </DropdownMenuItem>
            )}
            <DropdownMenuItem asChild data-testid="download-notice-pdf">
              <a href="/NOTICE_APP_MOBILE_KADOLOGISTIQUE.pdf" download className="cursor-pointer">
                <FileDown className="mr-2 h-4 w-4" />
                Notice d'utilisation
              </a>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="mobile-logout">
              <LogOut className="mr-2 h-4 w-4" />
              Déconnexion
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      {/* Main content */}
      <main className="pt-14">
        <Suspense fallback={null}>
          {children}
        </Suspense>
      </main>

      {/* Bottom navigation */}
      <nav className="mobile-nav" data-testid="mobile-bottom-nav">
        <div className="flex justify-around items-center max-w-md mx-auto relative">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            
            // Bouton Remettre spécial - plus grand et centré
            if (item.isMain) {
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid="mobile-nav-remettre"
                  className={`
                    flex flex-col items-center justify-center
                    w-16 h-16 -mt-6 rounded-full
                    ${isActive 
                      ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-300' 
                      : 'bg-emerald-500 text-white shadow-lg shadow-emerald-200'
                    }
                    transition-all transform hover:scale-105 active:scale-95
                  `}
                >
                  <Icon className="h-6 w-6" />
                  <span className="text-[10px] mt-0.5 font-medium">{item.label}</span>
                </Link>
              );
            }
            
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`mobile-nav-${item.path.split('/').pop() || 'home'}`}
                className={`mobile-nav-item ${isActive ? 'active' : ''} relative`}
              >
                <div className="relative">
                  <Icon className="h-5 w-5" />
                  {item.path === '/mobile/reception' && receptionsCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] flex items-center justify-center bg-orange-500 text-white text-[10px] font-bold rounded-full px-1">
                      {receptionsCount > 9 ? '9+' : receptionsCount}
                    </span>
                  )}
                </div>
                <span className="text-xs mt-1">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default MobileLayout;
